import { useEffect, useState } from "react"
import { Alert, Button, Col, FloatingLabel, Form, Modal, Row, Spinner, Table } from "react-bootstrap"
import {
  createUschovaSkladovaPozice,
  deleteUschovaSkladovaPozice,
  fetchUschovaDalsiKodPozice,
  fetchUschovaNastaveni,
  fetchUschovaSkladovePozice,
  updateUschovaNastaveni,
  updateUschovaSkladovaPozice,
} from "../services/api/uschovy"
import { printStoragePositionLabel } from "../components/StoragePositionLabelPrint"

const EMPTY_FORM = { id_pozice: 0, kod_ean: "", nazev: "" }
const DEFAULT_LIMITS = { limit_leto: "1,6", limit_zima: "4", limit_celorok: "4" }
const formatLimit = (value, fallback) => {
  const number = Number(String(value ?? "").replace(",", "."))
  return Number.isFinite(number) ? String(number).replace(".", ",") : fallback
}

const StoragePositions = ({ tabActive }) => {
  const [items, setItems] = useState([])
  const [form, setForm] = useState(EMPTY_FORM)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [generatingCode, setGeneratingCode] = useState(false)
  const [validated, setValidated] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")
  const [deleteItem, setDeleteItem] = useState(null)
  const [limits, setLimits] = useState(DEFAULT_LIMITS)
  const [limitsSaving, setLimitsSaving] = useState(false)
  const [limitsValidated, setLimitsValidated] = useState(false)
  const [limitsError, setLimitsError] = useState("")
  const [limitsSuccess, setLimitsSuccess] = useState("")

  const loadItems = async ({ clearMessages = true } = {}) => {
    setLoading(true)
    if (clearMessages) {
      setError("")
      setSuccess("")
    }
    try {
      const [rows, settings] = await Promise.all([fetchUschovaSkladovePozice(), fetchUschovaNastaveni()])
      setItems(Array.isArray(rows) ? rows : [])
      setLimits({
        limit_leto: formatLimit(settings?.limit_leto, DEFAULT_LIMITS.limit_leto),
        limit_zima: formatLimit(settings?.limit_zima, DEFAULT_LIMITS.limit_zima),
        limit_celorok: formatLimit(settings?.limit_celorok, DEFAULT_LIMITS.limit_celorok),
      })
    } catch (requestError) {
      setError(requestError.message || "Nepodařilo se načíst skladové pozice.")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (tabActive) loadItems()
  }, [tabActive])

  const resetForm = () => {
    setForm(EMPTY_FORM)
    setValidated(false)
  }

  const handleGenerateCode = async () => {
    setGeneratingCode(true)
    setError("")
    setSuccess("")
    try {
      const response = await fetchUschovaDalsiKodPozice()
      setForm((prev) => ({ ...prev, kod_ean: String(response?.kod_ean || "") }))
    } catch (requestError) {
      setError(requestError.message || "Kód skladové pozice se nepodařilo vygenerovat.")
    } finally {
      setGeneratingCode(false)
    }
  }

  const handleLimitsSubmit = async (event) => {
    event.preventDefault()
    setLimitsValidated(true)
    setLimitsError("")
    setLimitsSuccess("")
    if (!event.currentTarget.checkValidity()) return
    setLimitsSaving(true)
    try {
      await updateUschovaNastaveni(limits)
      setLimitsSuccess("Limity hloubky dezénu byly uloženy.")
      setLimitsValidated(false)
    } catch (requestError) {
      setLimitsError(requestError.message || "Limity hloubky dezénu se nepodařilo uložit.")
    } finally {
      setLimitsSaving(false)
    }
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    setValidated(true)
    setError("")
    setSuccess("")
    if (!event.currentTarget.checkValidity()) return

    const payload = {
      kod_ean: form.kod_ean.trim(),
      nazev: form.nazev.trim().toUpperCase(),
    }
    setSaving(true)
    try {
      if (form.id_pozice) {
        await updateUschovaSkladovaPozice(form.id_pozice, payload)
        setSuccess("Skladová pozice byla upravena.")
      } else {
        await createUschovaSkladovaPozice(payload)
        setSuccess("Skladová pozice byla přidána.")
      }
      resetForm()
      await loadItems({ clearMessages: false })
    } catch (requestError) {
      setError(requestError.message || "Skladovou pozici se nepodařilo uložit.")
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteItem) return
    setSaving(true)
    setError("")
    try {
      await deleteUschovaSkladovaPozice(deleteItem.id_pozice)
      setDeleteItem(null)
      setSuccess("Skladová pozice byla odstraněna.")
      await loadItems({ clearMessages: false })
    } catch (requestError) {
      setError(requestError.message || "Skladovou pozici se nepodařilo odstranit.")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="pt-2">
      <Row className="mb-4">
        <Col xl={8} lg={10}>
          <h5 className="mb-1">Limity hloubky dezénu</h5>
          <p className="text-muted small mb-3">Hodnoty v milimetrech používané v reportu Dezény pod limitem.</p>
          <Form noValidate validated={limitsValidated} onSubmit={handleLimitsSubmit}>
            <Row className="g-2">
              {[
                ["limit_leto", "Léto"],
                ["limit_zima", "Zima"],
                ["limit_celorok", "Celoroční"],
              ].map(([name, label]) => (
                <Col xs={12} sm={4} key={name}>
                  <FloatingLabel label={`${label} (mm)`}>
                    <Form.Control
                      type="text"
                      inputMode="decimal"
                      name={name}
                      value={limits[name]}
                      pattern="^(20([,.]0)?|[0-9]{1,2}([,.][0-9])?)$"
                      maxLength={4}
                      required
                      onChange={(event) => setLimits((prev) => ({ ...prev, [name]: event.target.value.replace(".", ",").replace(/[^0-9,]/g, "") }))}
                      title="Zadejte hodnotu od 0 do 20 mm s nejvýše jedním desetinným místem."
                    />
                  </FloatingLabel>
                </Col>
              ))}
            </Row>
            <Button type="submit" className="mt-3" disabled={limitsSaving || loading}>
              <i className="bi bi-floppy me-2"></i>{limitsSaving ? "Ukládám..." : "Uložit limity"}
            </Button>
          </Form>
          {limitsError ? <Alert variant="danger" className="mt-3 mb-0">{limitsError}</Alert> : null}
          {limitsSuccess ? <Alert variant="success" className="mt-3 mb-0">{limitsSuccess}</Alert> : null}
        </Col>
      </Row>
      <hr className="my-4" />
      <Row className="g-4">
        <Col xl={4} lg={5}>
          <h5 className="mb-1">Převodník skladových pozic</h5>
          <p className="text-muted small mb-3">Přiřazení čárového kódu EAN-13 k názvu fyzické pozice.</p>
          <Form noValidate validated={validated} onSubmit={handleSubmit}>
            <Row className="g-2">
              <Col xs={8}>
                <FloatingLabel label="EAN pozice">
                  <Form.Control
                    inputMode="numeric"
                    pattern="^95[0-9]{11}$"
                    maxLength={13}
                    required
                    value={form.kod_ean}
                    onChange={(event) => setForm((prev) => ({ ...prev, kod_ean: event.target.value.replace(/\D/g, "").slice(0, 13) }))}
                    title="Zadejte platný 13místný kód pozice začínající číslicemi 95."
                  />
                </FloatingLabel>
              </Col>
              <Col xs={4} className="d-grid">
                <Button type="button" variant="outline-primary" onClick={handleGenerateCode} disabled={saving || generatingCode} title="Vygenerovat další EAN-13 kód pozice">
                  <i className="bi bi-upc-scan me-2"></i>{generatingCode ? "Generuji..." : "Generovat"}
                </Button>
              </Col>
              <Col xs={12}>
                <FloatingLabel label="Název pozice">
                  <Form.Control
                    maxLength={50}
                    required
                    value={form.nazev}
                    onChange={(event) => setForm((prev) => ({ ...prev, nazev: event.target.value.toUpperCase() }))}
                  />
                </FloatingLabel>
              </Col>
            </Row>
            <div className="d-flex gap-1 mt-3">
              <Button type="submit" disabled={saving}>
                <i className="bi bi-floppy me-2"></i>{form.id_pozice ? "Uložit" : "Přidat"}
              </Button>
              {form.id_pozice ? (
                <Button type="button" variant="secondary" onClick={resetForm} disabled={saving}>
                  <i className="bi bi-arrow-left me-2"></i>Zpět
                </Button>
              ) : null}
            </div>
          </Form>
          {error ? <Alert variant="danger" className="mt-3 mb-0">{error}</Alert> : null}
          {success ? <Alert variant="success" className="mt-3 mb-0">{success}</Alert> : null}
        </Col>

        <Col xl={8} lg={7}>
          <div className="d-flex align-items-center justify-content-between mb-2">
            <h5 className="mb-0">Evidované pozice</h5>
            <Button type="button" variant="outline-secondary" onClick={() => loadItems()} disabled={loading || saving}>
              <i className="bi bi-arrow-clockwise me-2"></i>Obnovit
            </Button>
          </div>
          {loading ? (
            <div className="d-flex align-items-center gap-2 text-muted small py-3"><Spinner size="sm" />Načítám pozice...</div>
          ) : (
            <Table responsive hover striped className="align-middle">
              <thead><tr><th>Pozice</th><th>EAN</th><th className="text-end">Akce</th></tr></thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id_pozice}>
                    <td>{item.nazev}</td>
                    <td className="font-monospace">{item.kod_ean}</td>
                    <td className="text-end text-nowrap">
                      <Button type="button" variant="outline-secondary" size="sm" className="me-1" title="Tisk štítku skladové pozice" onClick={() => printStoragePositionLabel(item)}>
                        <i className="bi bi-printer"></i>
                      </Button>
                      <Button type="button" variant="outline-primary" size="sm" className="me-1" title="Upravit pozici" onClick={() => { setForm({ id_pozice: Number(item.id_pozice), kod_ean: String(item.kod_ean), nazev: String(item.nazev) }); setValidated(false); setError(""); setSuccess("") }}>
                        <i className="bi bi-pencil-square"></i>
                      </Button>
                      <Button type="button" variant="outline-danger" size="sm" title="Odstranit pozici" onClick={() => setDeleteItem(item)}>
                        <i className="bi bi-trash"></i>
                      </Button>
                    </td>
                  </tr>
                ))}
                {!items.length ? <tr><td colSpan={3} className="text-center text-muted py-4">Nejsou evidované žádné skladové pozice.</td></tr> : null}
              </tbody>
            </Table>
          )}
        </Col>
      </Row>

      <Modal show={Boolean(deleteItem)} onHide={() => !saving && setDeleteItem(null)} centered>
        <Modal.Header closeButton className="border-0"><Modal.Title className="text-primary">Skladová pozice</Modal.Title></Modal.Header>
        <Modal.Body>
          <Alert variant="warning" className="mb-0 d-flex align-items-center">
            <i className="bi bi-exclamation-triangle fs-3 me-3"></i>
            <span>Chcete odstranit pozici <strong>{deleteItem?.nazev}</strong>? Změna je nevratná.</span>
          </Alert>
        </Modal.Body>
        <Modal.Footer className="border-0 justify-content-start">
          <Button variant="danger" onClick={handleDelete} disabled={saving}><i className="bi bi-trash me-2"></i>Odstranit</Button>
          <Button variant="secondary" onClick={() => setDeleteItem(null)} disabled={saving}><i className="bi bi-arrow-left me-2"></i>Zpět</Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default StoragePositions
